import { useMemo, useState } from 'react';
import { COUNTRIES, countryFlag, regionsByCountry, type CountryCode } from 'shared';
import Icon from './Icon';
import Modal from './Modal';
import { haptic } from './telegram';

interface Props {
  label: string;
  value: string;
  onChange: (region: string) => void;
  /** Пункт «Любой регион» сверху — для необязательных фильтров. */
  allowEmpty?: string;
}

/*
 * Страна → её регионы берутся из `regionsByCountry` в shared, а не
 * выводятся здесь заново из `CITIES`. Свой такой вывод в компоненте уже
 * приводил к тому, что справочники расходились, и часть регионов
 * переставала быть доступной в фильтрах. Порядок стран (Россия первой,
 * дальше по алфавиту) тоже задан там — здесь его повторять незачем.
 */
const BY_COUNTRY = new Map<CountryCode, string[]>(regionsByCountry());
const COUNTRY_ORDER = [...BY_COUNTRY.keys()];
const ALL_REGIONS = COUNTRY_ORDER.flatMap((cc) => BY_COUNTRY.get(cc)!.map((r) => ({ region: r, country: cc })));

/**
 * Выбор региона: страна → область.
 *
 * Нативный `<select>` с сотней с лишним пунктов в WebView Telegram
 * прокручивается через раз и открывается каждый раз на своей высоте,
 * поэтому список живёт в модальном окне со своим скроллом.
 */
export default function RegionPicker({ label, value, onChange, allowEmpty }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState<CountryCode | null>(null);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return ALL_REGIONS.filter((r) => r.region.toLowerCase().includes(q)).slice(0, 60);
  }, [query]);

  function openPicker() {
    setQuery('');
    setCountry(null);
    setOpen(true);
  }

  function pick(region: string) {
    haptic('light');
    onChange(region);
    setOpen(false);
  }

  const regionsOfCountry = country ? BY_COUNTRY.get(country) : null;

  return (
    <>
      <button type="button" className="picker-trigger" onClick={openPicker}>
        <span className="picker-trigger__value">{value || allowEmpty || 'Не выбрано'}</span>
        <Icon name="down" size={16} />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={label}>
        <div className="field" style={{ marginBottom: 8 }}>
          <input
            placeholder="Поиск региона"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="picker-scroll">
        {searchResults ? (
          <div className="menu-list">
            {searchResults.length === 0 && <div className="empty">Регион не найден</div>}
            {searchResults.map((r) => (
              <button
                key={r.region}
                type="button"
                className={`menu-item${r.region === value ? ' active' : ''}`}
                onClick={() => pick(r.region)}
              >
                {countryFlag(r.country)} {r.region}
              </button>
            ))}
          </div>
        ) : (
          <>
            {country && (
              <button
                type="button"
                className="menu-item"
                style={{ marginBottom: 4 }}
                onClick={() => setCountry(null)}
              >
                <Icon name="back" size={16} />
                Назад
              </button>
            )}

            {!country && (
              <div className="menu-list">
                {allowEmpty && (
                  <button
                    type="button"
                    className={`menu-item${value === '' ? ' active' : ''}`}
                    onClick={() => pick('')}
                  >
                    {allowEmpty}
                  </button>
                )}
                {COUNTRY_ORDER.map((cc) => (
                  <button key={cc} type="button" className="menu-item" onClick={() => setCountry(cc)}>
                    {COUNTRIES[cc].flag} {COUNTRIES[cc].name}
                    <span className="meta" style={{ marginLeft: 'auto' }}>
                      {BY_COUNTRY.get(cc)!.length}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {country && regionsOfCountry && (
              <div className="menu-list">
                {regionsOfCountry.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`menu-item${r === value ? ' active' : ''}`}
                    onClick={() => pick(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        </div>
      </Modal>
    </>
  );
}

/**
 * Мультивыбор регионов — белые и чёрные списки групп видимости.
 *
 * До 31 августа 2026 здесь была стена чипов по всему справочнику. Пока
 * регионов было десять, это работало; после расширения на СНГ их стало
 * 145, и выбрать нужный стало нельзя физически. Тот же модальный список
 * с поиском, что и у одиночного выбора, плюс выбранное показывается
 * чипами снаружи — их немного, и снять лишний надо в один тап.
 */
export function RegionMultiPicker({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const groups = useMemo(() => regionsByCountry(), []);
  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return groups
      .flatMap(([cc, rs]) => rs.map((r) => ({ region: r, country: cc })))
      .filter((r) => r.region.toLowerCase().includes(q))
      .slice(0, 60);
  }, [query, groups]);

  function toggle(r: string) {
    haptic('light');
    onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  }

  return (
    <div className="field">
      <label>{label}</label>
      {hint && <div className="meta" style={{ marginBottom: 8 }}>{hint}</div>}

      {value.length > 0 && (
        <div className="chips" style={{ marginBottom: 8 }}>
          {value.map((r) => (
            <button key={r} type="button" className="chip on" onClick={() => toggle(r)}>
              {r} ×
            </button>
          ))}
        </div>
      )}

      <button type="button" className="picker-trigger" onClick={() => { setQuery(''); setOpen(true); }}>
        <span className="picker-trigger__value">
          {value.length === 0 ? 'Без ограничения' : `Выбрано: ${value.length}`}
        </span>
        <Icon name="down" size={16} />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={label}>
        <div className="field" style={{ marginBottom: 8 }}>
          <input
            placeholder="Поиск региона"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="picker-scroll menu-list">
          {found
            ? found.map((r) => (
                <button
                  key={r.region}
                  type="button"
                  className={`menu-item${value.includes(r.region) ? ' active' : ''}`}
                  onClick={() => toggle(r.region)}
                >
                  {COUNTRIES[r.country].flag} {r.region}
                </button>
              ))
            : groups.map(([cc, rs]) => (
                <div key={cc}>
                  <div className="meta" style={{ margin: '8px 0 4px' }}>
                    {COUNTRIES[cc].flag} {COUNTRIES[cc].name}
                  </div>
                  {rs.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={`menu-item${value.includes(r) ? ' active' : ''}`}
                      onClick={() => toggle(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              ))}
        </div>
      </Modal>
    </div>
  );
}
